import { createSelector } from '@reduxjs/toolkit';
import type React from 'react';

import type { SliceStateFromReducer } from 'shared/types';
import type { TPreparedVitrinsCredit, TPreparedVitrinsOffer } from 'shared/types/TVitrinsService';

import type { vitrinsSlice } from 'entities/vitrins';
import { buildSummary } from 'entities/vitrins';

export type TVitrinsState = SliceStateFromReducer<typeof vitrinsSlice>;

type SummaryItem = {
  label: string;
  value: string;
  tooltip?: React.ReactNode;
};

export type BankItem = TPreparedVitrinsOffer & { summary: SummaryItem[] };

export type BankGroup = TPreparedVitrinsOffer & {
  otherOffers: BankItem[];
  summary: SummaryItem[];
};

export const selectPreparedVitrinsOffers = (state: TVitrinsState) => state.vitrinsOffers.preparedOffers;

export const selectUniqVitrinsOffers = createSelector(selectPreparedVitrinsOffers, (offers) => {
  const map: Record<string, BankGroup> = {};

  for (const offer of offers) {
    const org = offer.organization;
    const summary = buildSummary(offer);

    if (!map[org]) {
      map[org] = {
        ...offer,
        summary,
        otherOffers: [],
      };
    } else {
      map[org].otherOffers.push({
        ...offer,
        summary,
      });
    }
  }

  return Object.values(map);
});

export const selectMinRateFromVitrinsOffers = createSelector(selectUniqVitrinsOffers, (offers) => {
  return offers.reduce<number | null>((acc, offer) => {
    if (offer.type !== 'credit') {
      return acc;
    }

    const rate = (offer as TPreparedVitrinsCredit).minRate;

    if (typeof rate !== 'number' || !Number.isFinite(rate) || rate <= 0) {
      return acc;
    }

    if (acc === null) {
      return rate;
    }

    return Math.min(acc, rate);
  }, null);
});

export const selectVitrinsOffersCount = createSelector(selectPreparedVitrinsOffers, (offers) => {
  return offers.length;
});
